import React, { useState } from "react";
import { useRouter } from "next/router";
import useUser from "@src/features/app/hooks/useUser";
import useLanguage from "@src/features/contentDetail/hooks/useLanguage";
import { UserType } from "@src/types.generated";
import { useTranslation } from "@src/features/contentDetail/utils/translationSets";
import { getFirstNameAndLastInitial } from "@src/features/app/top-nav/utils";
import {
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@src/lib/ui/Sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import ArrowLogoutIcon from "@src/lib/icons/18px/arrow-logout";
import CircleUserOutlineIcon from "@src/lib/icons/18px/circle-user-outline";
import ChevronsOpenIcon from "@src/lib/icons/12px/chevrons-open";
import NotificationBell from "@src/features/app/top-nav/NotificationBell";
import { useAdminToolsMenu } from "@src/features/app/top-nav/AdminTools";
import AdminLeftNavSettingsMenuButton from "@src/features/app/left-nav/admin/AdminLeftNavSettingsMenuButton";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import useHasCustomerFacingAccountPlan from "@src/features/settings/accountPlan/useHasCustomerFacingAccountPlan";
import FileTextOutlineIcon from "@src/lib/icons/18px/file-text-outline";
import Link from "next/link";

type Props = {
  hideSettingsButton?: boolean;
  /** Trainee sidebar: profile links to /my-profile and staff get admin tools. */
  variant?: "admin" | "trainee";
};

const AdminLeftNavUserFooter: React.FC<Props> = ({
  hideSettingsButton = false,
  variant = "admin",
}) => {
  const router = useRouter();
  const { user } = useUser();
  const { isMobile } = useSidebar();
  const { userActionPermissions } = useActionPermissions();
  const hasAccountPlan = useHasCustomerFacingAccountPlan();
  const language = useLanguage();
  const [open, setOpen] = useState(false);

  const profileTranslation = useTranslation("Profile", language || "en");
  const logoutTranslation = useTranslation("Logout", language || "en");

  const { menuItem: adminToolsMenuItem, dialog: adminToolsDialog } =
    useAdminToolsMenu();
  const isTrainee = !user || user?.userType === UserType.Employee;
  const isTraineeNav = variant === "trainee";
  if (!user) return null;

  const displayName = getFirstNameAndLastInitial(user.name);

  return (
    <SidebarFooter className="p-2">
      <SidebarMenu>
        {!hideSettingsButton && !isTraineeNav && hasAccountPlan && (
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={router.pathname === "/account-plan"}
              tooltip="Account Plan"
            >
              <Link href="/account-plan">
                <FileTextOutlineIcon />
                Account Plan
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
        {!hideSettingsButton &&
          !isTraineeNav &&
          userActionPermissions?.changeOrgSettings && (
            <AdminLeftNavSettingsMenuButton />
          )}
        <SidebarMenuItem className="flex items-center gap-1">
          <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="default"
                className="min-w-0 flex-1 data-[state=open]:!bg-black-alpha-2"
                tooltip={displayName}
              >
                <CircleUserOutlineIcon />
                <span className="truncate">{displayName}</span>
                <ChevronsOpenIcon className="ml-auto !size-3 opacity-50 group-data-[collapsible=icon]:hidden" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="font-normal-v4">
                <div className="text-s-v4 font-medium-v4">{user.name}</div>
                <div className="text-2xs-v4 text-gray-9">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isTraineeNav ? (
                <>
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onClick={() => {
                        router.push("/my-profile");
                        setOpen(false);
                      }}
                    >
                      <CircleUserOutlineIcon />
                      {profileTranslation.text || "Profile"}
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                </>
              ) : (
                !isTrainee && (
                  <>
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        onClick={() => {
                          router.push({
                            pathname: "/users/[id]",
                            query: { id: user.id.toString() },
                          });
                          setOpen(false);
                        }}
                      >
                        <CircleUserOutlineIcon />
                        {profileTranslation.text || "Profile"}
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                  </>
                )
              )}
              {adminToolsMenuItem && (
                <>
                  <DropdownMenuGroup>{adminToolsMenuItem}</DropdownMenuGroup>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                onClick={() => {
                  router.push("/logout");
                  setOpen(false);
                }}
              >
                <ArrowLogoutIcon />
                {logoutTranslation.text || "Logout"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {!isTrainee && !isTraineeNav && (
            <div className="group-data-[collapsible=icon]:hidden">
              <NotificationBell />
            </div>
          )}
        </SidebarMenuItem>
      </SidebarMenu>
      {adminToolsDialog}
    </SidebarFooter>
  );
};

export default AdminLeftNavUserFooter;
